<script setup>
import { onMounted, onUnmounted, computed, ref } from 'vue'
import { api, config, configuration, authentication, dataLayer, hybrid, privacy, socket } from '@dpgradio/creative'
import { isDev, isStaging, socketHost, stationIdFromUrl } from './environment.js'
import { useGameStore, ROUND_STATUS } from './stores/game.js'
import GameInfo from './components/GameInfo.ce.vue'
import BingoCard from './components/BingoCard.ce.vue'
import GameOverlay from './components/GameOverlay.ce.vue'
import BingoConfirm from './components/BingoConfirm.ce.vue'
import ClaimWarning from './components/ClaimWarning.ce.vue'

const props = defineProps({
  gameId: { type: String, default: null },
  stationId: { type: String, default: null },
  theme: { type: String, default: null },
})

const gameId = props.gameId || new URLSearchParams(window.location.search).get('gameId')
const stationId = props.stationId || stationIdFromUrl() || 'qmusic_be'
const theme = props.theme || stationId

const gameStore = useGameStore()
const ready = ref(false)
const appEl = ref(null)
const showBingoConfirm = ref(false)
const canMark = computed(() => gameStore.isPlaying && !gameStore.isBingoClaimed && !showBingoConfirm.value)
const showBingoOverlay = computed(() => gameStore.isBingoClaimed)
const showLostOverlay = computed(() => gameStore.isEnded && gameStore.hasCard && !gameStore.isBingoClaimed)

let stationSocket = null
let gameSubscription = null
let cardSubscription = null

async function configure() {
  try {
    await configuration.retrieveConfigForStation(stationId)
  } catch (error) {
    console.error(`
      Could not load configuration for station "${stationId}".
      - Have you set up a configuration for this application in Dario (see [updateConfigSchema.js])?
      - Did you provide a valid station? (via the 'station-id' attribute or the 'stationId' URL parameter)

      ${error}
    `)
  }

  if (isDev()) {
    api.baseUrlOverride = 'http://localhost:3000'
    api.requestModifiers.push((request) => request.withHeader('X-Station-Id', configuration.stationId))
  }

  if (isStaging()) {
    api.baseUrlOverride = api.baseUrl.replace('api.', 'api.stag.')
  }

  try {
    const language = config('language')
    if (language) document.documentElement.lang = language
  } catch (_) {
    // config not available, continue with defaults
  }

  authentication.initialize()

  privacy.initialize()
  dataLayer.initialize()
  dataLayer.pushVirtualPageView()
}

function subscribeToCard(cardId) {
  if (!stationSocket || cardSubscription) return
  cardSubscription = stationSocket.subscribe({ bingo_card: cardId })
  cardSubscription.on('mark', ({ card_square_id }) => gameStore.setSquareMarked(card_square_id, true), {
    backlog: true,
    scope: 'gigya',
  })
  cardSubscription.on('unmark', ({ card_square_id }) => gameStore.setSquareMarked(card_square_id, false), {
    backlog: true,
    scope: 'gigya',
  })
}

async function handleGetCard() {
  try {
    await authentication.require()
    if (stationSocket) stationSocket.authenticate('gigya', { token: authentication.radioToken })
    await gameStore.createOrLoadCard(gameId)
    if (gameStore.card) subscribeToCard(gameStore.card.id)
  } catch {
    // error is set in the store
  }
}

async function handleToggleSquare(cardSquare) {
  await gameStore.toggleSquare(cardSquare)
  if (gameStore.canClaimBingo) {
    showBingoConfirm.value = true
  }
}

async function handleConfirmBingo() {
  showBingoConfirm.value = false
  await gameStore.claimBingo()
}

function handleCancelBingo() {
  showBingoConfirm.value = false
}

function handleNewCard() {
  if (cardSubscription) {
    cardSubscription.unsubscribe()
    cardSubscription = null
  }
  gameStore.card = null
  gameStore.error = null
  gameStore.failedClaim = null
  handleGetCard()
}

function applyFullscreenBackground() {
  if (!document.body.classList.contains('bingo-in-fullscreen')) return
  if (!appEl.value) return
  const color = getComputedStyle(appEl.value).getPropertyValue('--bingo-primary-dark').trim()
  if (color) document.body.style.backgroundColor = color
}

onMounted(async () => {
  applyFullscreenBackground()
  await configure()
  if (hybrid.isNativeApp()) {
    // In the app we wait to see if we already have a radio token, on
    // web this happens sync so we don't need to wait for it.
    //
    // Any no-token rejection is just that, it's not an error
    await hybrid.appLoaded().catch(() => {})
  }

  if (!gameId) {
    gameStore.error = 'Geen game-id attribuut geconfigureerd.'
    ready.value = true
    return
  }

  await gameStore.loadGame(gameId)

  if (gameStore.round && authentication.isLoggedIn()) {
    try {
      await gameStore.createOrLoadCard(gameId)
    } catch {
      // error is set in the store
    }
    if (gameStore.canClaimBingo) {
      showBingoConfirm.value = true
    }
  }

  ready.value = true

  if (gameStore.game) {
    stationSocket = socket.connect(stationId, socketHost(stationId))

    if (authentication.isLoggedIn()) {
      stationSocket.authenticate('gigya', { token: authentication.radioToken })
    }

    gameSubscription = stationSocket.subscribe({ bingo_game: Number(gameId) })
    gameSubscription.on(
      'round_status',
      async ({ status, round_id }) => {
        console.log('[bingo] round_status event received', { status, round_id })
        gameStore.updateRoundStatus(status, round_id)
        if (status === ROUND_STATUS.ENDED) {
          if (gameStore.hasCard) await gameStore.reloadCard()
          await gameStore.reloadGame(gameId)
        }
      },
      { backlog: 1 }
    )

    if (gameStore.card) {
      subscribeToCard(gameStore.card.id)
    }
  }
})

onUnmounted(() => {
  if (gameSubscription) gameSubscription.unsubscribe()
  if (cardSubscription) cardSubscription.unsubscribe()
  document.body.style.backgroundColor = ''
})
</script>

<template>
  <div ref="appEl" class="bingo-app" :class="`theme-${theme}`">
    <div v-if="!ready" class="loading">
      <p>Laden...</p>
    </div>

    <div v-else-if="gameStore.error && !gameStore.game" class="error">
      <p>{{ gameStore.error }}</p>
    </div>

    <template v-else-if="gameStore.game">
      <GameInfo
        v-if="!gameStore.hasCard"
        :game="gameStore.game"
        :loading="gameStore.loading"
        :hasRound="gameStore.hasActiveRound"
        @getCard="handleGetCard"
      />

      <template v-if="gameStore.hasCard">
        <BingoCard
          :gridSquares="gameStore.gridSquares"
          :gridCols="gameStore.game.grid_cols"
          :canMark="canMark"
          :roundStatus="gameStore.roundStatus"
          @toggleSquare="handleToggleSquare"
        />

        <p v-if="gameStore.error" class="card-error">{{ gameStore.error }}</p>
      </template>

      <ClaimWarning
        v-if="gameStore.failedClaim && !gameStore.isEnded"
        :attemptsLeft="gameStore.failedClaim.attemptsLeft"
        :wrongfullyMarkedCount="gameStore.failedClaim.wrongfullyMarkedCount"
        @dismiss="gameStore.failedClaim = null"
      />
      <BingoConfirm v-if="showBingoConfirm" @confirm="handleConfirmBingo" @cancel="handleCancelBingo" />
      <GameOverlay
        v-if="showBingoOverlay"
        type="bingo"
        :winTitle="gameStore.game?.win_title"
        :winMessage="gameStore.game?.win_message"
      />
      <GameOverlay
        v-if="showLostOverlay"
        type="lost"
        :loseTitle="gameStore.game?.lose_title"
        :loseMessage="gameStore.game?.lose_message"
        :hasNextRound="gameStore.hasNextRound"
        @newCard="handleNewCard"
      />
    </template>
  </div>
</template>

<style>
@import '@dpgradio/creative/styles/all';
@import './themes.css';

:host {
  display: block;
}

.bingo-app {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading,
.error {
  text-align: center;
}

.loading p,
.error p {
  font-family: var(--bingo-font-body);
  color: white;
  font-size: 1rem;
}

.card-error {
  font-family: var(--bingo-font-body);
  color: #ff6b6b;
  text-align: center;
  margin-top: 16px;
  font-size: 0.9rem;
}
</style>
